/**
 * ProductsView - Full-page view for products management
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { message } from "antd";
import { Form } from "antd";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../services/productService";
import ProductList from "../components/ProductList";
import ProductForm from "../components/ProductForm";
import RequireAdmin from "../../../components/ProtectedRoute";
import {
  PageHeader,
  PageFilters,
} from "../../../shared/components/pageShell";

const normalizeActiveFilter = (value) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return undefined;
};

/**
 * ProductsView Component
 */
const ProductsView = () => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [apiFilters, setApiFilters] = useState({
    category: "",
    active: true,
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getProducts({
        ...apiFilters,
        search: debouncedSearch || undefined,
      });
      if (result.success) {
        setProducts(result.data || []);
      } else {
        setError(result.error || "Failed to fetch products");
        setProducts([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [apiFilters, debouncedSearch]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleApiFilterChange = (field, value) => {
    setApiFilters((prev) => ({
      ...prev,
      [field]:
        field === "active" ? normalizeActiveFilter(value) : value,
    }));
  };

  const handleOpenForm = (product = null) => {
    setEditProduct(product);
    if (product) {
      form.setFieldsValue(product);
    } else {
      form.resetFields();
      // Set default values for new product
      form.setFieldsValue({
        category: "other",
        active: true,
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditProduct(null);
    form.resetFields();
  };

  const handleSubmitForm = async (values) => {
    setFormLoading(true);
    try {
      let result;
      if (editProduct) {
        result = await updateProduct(editProduct.id, values);
      } else {
        result = await createProduct(values);
      }

      if (result.success) {
        message.success(
          `Product ${editProduct ? "updated" : "created"} successfully`
        );
        handleCloseForm();
        fetchProducts();
      } else {
        message.error(result.error || "Failed to save product");
      }
    } catch (err) {
      message.error(err.message || "Failed to save product");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${product.name}"? This will set it to inactive.`
      )
    ) {
      try {
        const result = await deleteProduct(product.id);
        if (result.success) {
          message.success("Product deleted successfully");
          fetchProducts();
        } else {
          message.error(result.error || "Failed to delete product");
        }
      } catch (err) {
        message.error(err.message || "Failed to delete product");
      }
    }
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <RequireAdmin>
      <>
        <PageHeader
          title="Products Management"
          actions={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenForm()}
              sx={{
                width: { xs: "100%", sm: "auto" },
                flex: { xs: 1, md: "none" },
                textTransform: "none",
                fontWeight: 600,
                minHeight: { xs: 44, md: 36 },
              }}
            >
              Add Product
            </Button>
          }
        />

        <PageFilters>
          <TextField
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 250 },
              flex: { sm: 1 },
              bgcolor: "background.paper",
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              bgcolor: "background.paper",
            }}
          >
            <InputLabel>Category</InputLabel>
            <Select
              value={apiFilters.category}
              onChange={(e) =>
                handleApiFilterChange("category", e.target.value)
              }
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="supplies">Supplies</MenuItem>
              <MenuItem value="services">Services</MenuItem>
              <MenuItem value="protection">Protection</MenuItem>
              <MenuItem value="packing">Packing</MenuItem>
              <MenuItem value="transport">Transport</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 120 },
              bgcolor: "background.paper",
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={
                apiFilters.active === undefined ? "" : apiFilters.active
              }
              onChange={(e) =>
                handleApiFilterChange("active", e.target.value)
              }
              label="Status"
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
              <MenuItem value="">All</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchProducts}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Refresh
          </Button>
        </PageFilters>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductList
            products={products}
            loading={loading}
            onEditProduct={handleOpenForm}
            onDeleteProduct={handleDeleteProduct}
            onRefresh={fetchProducts}
          />
        )}

        <Dialog
          open={formOpen}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editProduct ? "Edit Product" : "Create New Product"}
          </DialogTitle>
          <DialogContent>
            <ProductForm
              form={form}
              onFinish={handleSubmitForm}
              onCancel={handleCloseForm}
              loading={formLoading}
              initialValues={editProduct || {}}
            />
          </DialogContent>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleNotificationClose}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </>
    </RequireAdmin>
  );
};

export default ProductsView;






